import { EventEmitter } from 'events';

import Compressor from 'compressorjs';
import localforage from 'localforage';

const DEFAULT_FONT = '50px EuphoriaScript Regular';

export function getTextWidth(text: string, font = DEFAULT_FONT): number {
  // re-use canvas object for better performance
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context!.font = font;
  const metrics = context!.measureText(text);
  return metrics.width;
}

export function textToBase64(text: string): string {
  // 创建 canvas 对象
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // 设置画布大小
  canvas.width = getTextWidth(text);
  canvas.height = 60;

  // 绘制文本
  ctx.font = DEFAULT_FONT;
  ctx.fillText(text, 0, 45);

  // 将绘制结果转换为 Base64 编码的图像
  const base64Image = canvas.toDataURL('image/png');

  return base64Image;
}

export const getBase64 = (img: File, callback: (url: string) => void): void => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

export const compressImage = (file: File, options?: Compressor.Options): Promise<File | Blob> => {
  return new Promise((resolve) => {
    new Compressor(file, {
      quality: 0.6,
      convertSize: 1000000,
      ...options,
      success: (result) => {
        resolve(result);
      },
      error(error: Error) {
        console.log(error);
      }
    });
  });
};

type TDate = 'h' | 'w' | 'd' | 'm' | 's';
type TExpire = `${number}_${TDate}`;
interface IStorage {
  value: any;
  key: string;
  expire?: TExpire;
}

export const storageUtil = {
  set(data: IStorage): void {
    if (data.expire) {
      const date = { expire: 0 };
      data?.expire && this.processExpire(data.expire, date);
      localStorage.setItem(data.key, JSON.stringify({ expire: date.expire, value: data.value }));
    } else {
      localStorage.setItem(data.key, JSON.stringify(data.value));
    }
  },
  get(key: string): Omit<IStorage, 'key'> | null {
    let res = localStorage.getItem(key) as any;
    res && (res = JSON.parse(res));

    if (res?.expire && res.expire < new Date().getTime()) {
      localStorage.removeItem(key);
      res = null;
    }
    return res;
  },
  delete(key: string): void {
    localStorage.removeItem(key);
  },
  processExpire(expire: TExpire, date: { expire: number }): void {
    const [time, timerUnit] = expire.split('_');
    switch (timerUnit) {
      case 's':
        date.expire = Number(time) * 1000;
        break;
      case 'm':
        date.expire = Number(time) * 1000 * 60;
        break;
      case 'h':
        date.expire = Number(time) * 1000 * 60 * 60;
        break;
      case 'd':
        date.expire = Number(time) * 1000 * 60 * 60 * 24;
        break;
      case 'w':
        date.expire = Number(time) * 1000 * 60 * 60 * 24 * 7;
        break;
    }
    date.expire += new Date().getTime();
  }
};

//使用JSON
export function setStyle(element: HTMLElement, styleObject: { [x: string]: any }): void {
  for (const key in styleObject) {
    element.style[key as any] = styleObject[key];
  }
}

export function getTextFromTemplate(templateStr: string, vars: Record<string, string>): string {
  const result = templateStr.replace(/{[a-zA-Z0-9_]+}/g, (match) => {
    const key = match.slice(1, -1);
    const val = vars[key] || '';
    return val;
  });

  return result;
}
export async function generateImg(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = src;
  img.crossOrigin = 'anonymous';
  return new Promise((resolve) => {
    img.onload = () => {
      resolve(img);
    };
  });
}
export const getHtmlBody = (html: string): string => {
  const reg = /<body[^>]*>([\s\S]*)<\/body>/;
  const match = html.match(reg);
  return match ? match[1] : html;
};
export const getWrapHtml = (html: string): string => {
  const body = getHtmlBody(html);
  return `<html><head><style>html {font-size:16px;} h2 {font-size: 2.18em;line-height: 1.37em;padding-top: .342em;}h3 {font-size: 1.75em;line-height: 1.86em;padding-top: .171em;}h4 {font-size: 1.31em;line-height: 1.24em; padding-top: .286em;} body {line-height:1.5;width: 15.92cm;min-height: 29.7cm;margin: 0;box-sizing: border-box;} ol,ul,menu {margin: 0;padding: 0;} li>ol,ul,menu {padding-left: 40px;}ol ol ol ol ol {list-style-type: upper-roman;}ol ol ol ol {list-style-type: upper-latin;}ol ol ol {    list-style-type: lower-roman;}ol ol {    list-style-type: lower-latin;}ol {    list-style-type: decimal;}ul ul ul {  list-style-type: square;}ul ul {  list-style-type: circle;}ul {  list-style-type: disc;}</style><meta charset="UTF-8"></head><body>${body}</body></html>`;
};
export const replacePtoDiv = (html: string): string => {
  const reg = /<p\b[^>]*>(.*?)<\/p>/gi;
  const match = html.match(reg);
  return match ? html.replace(reg, '<div>$1</div>') : html;
};
export const replaceFont = (html: string, font?: string): string => {
  const htmlEl = document.createElement('html');
  htmlEl.innerHTML = html;
  [].slice.call(htmlEl.querySelectorAll('*')).forEach((t: HTMLElement) => {
    if (t.style) {
      const fontFamily = t.style.fontFamily;
      t.style.fontFamily = fontFamily ? fontFamily : font || '';
    }
  });
  return htmlEl.innerHTML;
};
export const isEmptyHtml = (html: string): boolean => {
  const htmlBody = getHtmlBody(html);
  return !htmlBody || htmlBody === '<div>&nbsp;</div>';
};
export const handleHtml = (html: string): string => {
  return getWrapHtml(replaceFont(replacePtoDiv(html), ''));
};
export const eventBus = new EventEmitter();

export const deleteOtherLocalStorage = (key: string): void => {
  const keys = Object.keys(localStorage);
  localforage.keys().then((res) => {
    res?.forEach((item) => {
      if (item !== key && item.indexOf('temp') > -1) {
        localforage.removeItem(item);
      }
    });
  });
  keys.forEach((item) => {
    if (item !== key && item.indexOf('temp') > -1) {
      localStorage.removeItem(item);
    }
  });
};
